/**
 * Desktop shell env-map (AB#425).
 *
 * The Tauri shell launches the wrapped admin at `.../#env=local|qa|prod`. This
 * module resolves that env SYNCHRONOUSLY at import time and:
 *   - points the API client at the matching backend (setApiBase), and
 *   - exposes a per-env auth persist key so qa/prod/local tokens never bleed.
 *
 * A plain browser (no `#env=...`) is untouched: base stays `/api`, key `wpp-auth`.
 *
 * This module has no runtime imports on purpose: the persist key must resolve at
 * import time (authStore reads `authPersistKey`), and the client's `setApiBase`
 * call is applied from the admin bootstrap via `applyEnvApiBase()`.
 */

// Tauri injects `__TAURI_INTERNALS__` into the MAIN frame only, and the shell
// wraps the admin in a same-origin iframe — so without this, isTauri() is
// false in the wrapped admin and the plugin-http native transport (DECISIONS
// §12) never engages, breaking every API call from the desktop app. The IPC
// message handlers exist in sub-frames; only the JS glue is main-frame-only,
// so adopting the parent's internals restores invoke/plugins in the iframe.
// try/catch: a cross-origin parent (third-party embed) throws on access.
try {
  const w = window as unknown as Record<string, unknown>;
  const parent = window.parent as unknown as Record<string, unknown>;
  if (!w.__TAURI_INTERNALS__ && window.parent !== window && parent.__TAURI_INTERNALS__) {
    w.__TAURI_INTERNALS__ = parent.__TAURI_INTERNALS__;
  }
} catch {
  // cross-origin parent — plain browser embed, nothing to adopt
}

/** Baked backend base URL per shell env. */
export const API_BASE_BY_ENV = {
  local: 'http://localhost:5150/api',
  qa: 'https://app-qa.wordpuppi.com/api',
  prod: 'https://app.wordpuppi.com/api',
} as const;

export type DesktopEnv = keyof typeof API_BASE_BY_ENV;

/**
 * Parse `#env=local|qa|prod` out of a location hash.
 * @param hash - hash string incl. leading `#` (defaults to the live location.hash).
 * @returns the env if present and known, else null (plain browser).
 */
export function resolveEnv(hash?: string): DesktopEnv | null {
  const raw = hash ?? (typeof location !== 'undefined' ? location.hash : '');
  if (!raw) return null;
  const env = new URLSearchParams(raw.replace(/^#/, '')).get('env');
  return env && env in API_BASE_BY_ENV ? (env as DesktopEnv) : null;
}

/** The auth localStorage key, namespaced per env. Plain browser → `wpp-auth`. */
export function persistKeyForEnv(env: DesktopEnv | null): string {
  return 'wpp-auth' + (env ? ':' + env : '');
}

/** Running inside the Tauri desktop shell? Gates desktop-only affordances. */
export function isTauri(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof (window as unknown as { __TAURI_INTERNALS__?: unknown }).__TAURI_INTERNALS__ !==
      'undefined'
  );
}

/**
 * AB#488/#489/#490: open a URL in the user's real default browser.
 *
 * Desktop (Tauri): wry ignores `window.open`/`target=_blank`, so hand off to
 * `tauri-plugin-opener` (name agreed with the Rust-side registration). Browser:
 * a plain new tab. The dynamic import keeps the plugin out of the web bundle
 * (same pattern as EditorDiskBar's dialog/fs imports).
 */
export async function openExternal(url: string): Promise<void> {
  if (isTauri()) {
    const { openUrl } = await import('@tauri-apps/plugin-opener');
    await openUrl(url);
  } else {
    window.open(url, '_blank', 'noopener');
  }
}

/**
 * Run `cb` each time the window regains focus — the "user came back from the
 * system browser" poke that drives checkout/GSC status re-polls (AB#489/#490).
 * Returns an unsubscribe fn; call it on unmount.
 */
export function onWindowFocus(cb: () => void): () => void {
  if (typeof window === 'undefined') return () => {};
  window.addEventListener('focus', cb);
  return () => window.removeEventListener('focus', cb);
}

/** sessionStorage key holding the desktop env, so it survives a hash-dropping nav. */
const ENV_STORAGE_KEY = 'wpp-env';

/**
 * Resolve the env for this session, persisting it across reloads (#608).
 *
 * TanStack Router rebuilds the URL on every in-admin navigation and drops the
 * `#env=prod` fragment the desktop shell injected. A later webview reload then
 * re-imports this module with a hashless URL → `resolveEnv()` would return null,
 * silently falling back to the `/api` base + bare `wpp-auth` key = a dropped
 * session on a dead backend. So: hash present → it wins and is persisted; hash
 * absent → fall back to the persisted value. A plain browser never writes the
 * key (it has no `#env`), so its no-hash → null contract is untouched, and per
 * env isolation (#425) holds because a shell reopened at a different `#env=`
 * overwrites the stored value.
 */
function resolveActiveEnv(): DesktopEnv | null {
  const fromHash = resolveEnv();
  try {
    if (fromHash) {
      sessionStorage.setItem(ENV_STORAGE_KEY, fromHash);
      return fromHash;
    }
    const stored = sessionStorage.getItem(ENV_STORAGE_KEY);
    return stored && stored in API_BASE_BY_ENV ? (stored as DesktopEnv) : null;
  } catch {
    // sessionStorage unavailable (SSR / privacy mode) — hash is the only source.
    return fromHash;
  }
}

/** Env resolved once, at module load — before the auth store's persist name is read. */
export const activeEnv: DesktopEnv | null = resolveActiveEnv();

/** Auth persist key for this session. Imported by authStore so it resolves deterministically. */
export const authPersistKey: string = persistKeyForEnv(activeEnv);
